/**
 * Epic 6-7: UTC storage (Story 6-7-1), local display (Story 6-7-2).
 * Default TZ: Africa/Algiers when unknown.
 */

/** Returns the timezone for display. Uses browser timezone or Africa/Algiers fallback. */
export function getDisplayTimezone(): string {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return tz || "Africa/Algiers";
  } catch {
    return "Africa/Algiers";
  }
}

/**
 * Format date-only (YYYY-MM-DD) or ISO 8601 datetime as calendar date.
 * Accepts YYYY-MM-DD or ISO 8601 (e.g. 2026-02-06T14:30:00Z); formats date part only.
 * No timezone conversion for date-only—display as UTC day. Uses browser locale.
 */
export function formatDate(dateString: string): string {
  if (!dateString || typeof dateString !== "string" || !dateString.trim()) {
    return "—";
  }
  const trimmed = dateString.trim();
  const date = new Date(trimmed.length === 10 ? trimmed + "T12:00:00Z" : trimmed);
  if (Number.isNaN(date.getTime())) {
    return "—";
  }
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeZone: "UTC",
  }).format(date);
}

/** Format ISO 8601 datetime (UTC) in user's local timezone for display. */
export function formatDateTime(isoString: string): string {
  if (!isoString || typeof isoString !== "string" || !isoString.trim()) {
    return "—";
  }
  const date = new Date(isoString.trim());
  if (Number.isNaN(date.getTime())) {
    return "—";
  }
  const tz = getDisplayTimezone();
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: tz,
  }).format(date);
}

/**
 * Format a date string as relative time (e.g. "2 hours ago").
 * Uses Intl.RelativeTimeFormat when available.
 * Works with UTC ISO strings; relative diff is timezone-invariant.
 */
export function formatTimeAgo(isoDateString: string): string {
  if (!isoDateString || typeof isoDateString !== "string" || !isoDateString.trim()) {
    return "—";
  }
  const date = new Date(isoDateString.trim());
  if (Number.isNaN(date.getTime())) {
    return "—";
  }
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffSec < 60) return "just now";
  if (diffMin < 60) return `${diffMin} minute${diffMin !== 1 ? "s" : ""} ago`;
  if (diffHour < 24) return `${diffHour} hour${diffHour !== 1 ? "s" : ""} ago`;
  if (diffDay < 7) return `${diffDay} day${diffDay !== 1 ? "s" : ""} ago`;

  try {
    const rtf = new Intl.RelativeTimeFormat("en", { numeric: "auto" });
    if (diffDay < 30) return rtf.format(-diffDay, "day");
    const diffMonth = Math.floor(diffDay / 30);
    if (diffMonth < 12) return rtf.format(-diffMonth, "month");
    const diffYear = Math.floor(diffMonth / 12);
    return rtf.format(-diffYear, "year");
  } catch {
    const tz = getDisplayTimezone();
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: "medium",
      timeZone: tz,
    }).format(date);
  }
}
